/**
 * Builds a copy-pasteable error report from any error source.
 *
 * Used by the FailureScreen (ErrorBoundary, RouteErrorBoundary) to provide
 * debug output.
 */
import { isProduction } from './env';
import { scrubUrlSecrets } from './scrubUrlSecrets';
import { stripLinkSegment } from './urlExposure';

/**
 * Strip absolute source paths from a string to prevent leaking bundle layout
 * (and therefore source-map locations / file structure) to anyone with copy
 * access to the crash screen. Replaces things that look like absolute POSIX
 * or Windows paths with a `[redacted]` placeholder, keeping the trailing
 * filename for debuggability.
 *
 * Exported for testing — callers should not need this directly.
 */
export function redactSourcePaths(text: string): string {
  return (
    text
      // POSIX absolute paths: /a/b/c/file.ts → [redacted]/file.ts
      .replace(/(\/[\w.-]+)+\/([\w.-]+\.(?:ts|tsx|js|jsx|mjs|cjs))/g, '[redacted]/$2')
      // Windows: C:\Users\...\file.ts → [redacted]\file.ts
      .replace(/([A-Za-z]:\\(?:[\w.-]+\\)+)([\w.-]+\.(?:ts|tsx|js|jsx|mjs|cjs))/g, '[redacted]\\$2')
  );
}

export interface ErrorReportInput {
  /** Error type label: "Application Error", "Server Error (500)", etc. */
  title: string;
  /** Human-readable error message */
  message: string;
  /** Error source */
  source: 'client' | 'server' | 'route';
  /** HTTP status code, if applicable */
  status?: number;
  /** Error object for stack trace extraction */
  error?: Error | unknown;
  /** React component stack from ErrorInfo */
  componentStack?: string;
  /** Request URL, e.g. "GET /api/packets/123/" */
  requestUrl?: string;
  /** Response body (string or pre-stringified JSON) */
  responseBody?: string;
  /** Number of retries attempted before failure */
  retryCount?: number;
  /** Server-assigned trace correlation ID */
  traceId?: string;
}

/**
 * Build a formatted, copy-pasteable error report string.
 *
 * Sections with no data are omitted entirely.
 * Reads window.location.href for the current page URL.
 */
export function buildErrorReport(input: ErrorReportInput): string {
  const lines: string[] = [];

  lines.push('Error Report');
  lines.push('============');

  // Timestamp
  lines.push(`Time: ${new Date().toISOString()}`);

  // Type
  lines.push(`Type: ${input.title}`);

  // Page URL
  if (typeof window !== 'undefined' && window.location?.href) {
    lines.push(`Page: ${window.location.href}`);
  }

  // HTTP status
  if (input.status != null) {
    lines.push(`Status: ${input.status}`);
  }

  // Request URL
  if (input.requestUrl) {
    lines.push(`Request: ${input.requestUrl}`);
  }

  // Trace ID
  if (input.traceId) {
    lines.push(`Trace ID: ${input.traceId}`);
  }

  // Retries
  if (input.retryCount != null && input.retryCount > 0) {
    lines.push(`Retries: ${input.retryCount}`);
  }

  // Message
  if (input.message) {
    lines.push('');
    lines.push('Message:');
    lines.push(`  ${input.message}`);
  }

  // Response body
  if (input.responseBody) {
    lines.push('');
    lines.push('Response:');
    for (const line of input.responseBody.split('\n')) {
      lines.push(`  ${line}`);
    }
  }

  // Stack trace — non-production only. In production these leak bundled
  // source paths (and therefore source-map URLs / file structure) to anyone
  // with copy access to the crash screen. Gate on VITE_ENV via isProduction()
  // — the mode axis, so a staging build still shows it — and redact absolute
  // paths as a backstop.
  if (!isProduction()) {
    const stack = input.error instanceof Error ? input.error.stack : undefined;
    if (stack) {
      lines.push('');
      lines.push('Stack Trace:');
      for (const line of stack.split('\n')) {
        lines.push(`  ${redactSourcePaths(line)}`);
      }
    }

    // Component stack
    if (input.componentStack) {
      lines.push('');
      lines.push('Component Stack:');
      for (const line of input.componentStack.split('\n')) {
        const trimmed = line.trim();
        if (trimmed) {
          lines.push(`  ${redactSourcePaths(trimmed)}`);
        }
      }
    }
  }

  // The report is copy-pasted into tickets and chat: URL secrets (`?claim=`,
  // magic-link keys, …) and the recipient-link segment (vingreact#717) never
  // leave in it, whichever line carries them (Page, Request, Message, stack).
  return stripLinkSegment(scrubUrlSecrets(lines.join('\n')));
}
